import { useEffect, useState } from "react";
import { api, formatErr, inr } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { HandCoins, RefreshCw } from "lucide-react";

const SLAB_LABELS = {
  site_visit_local: "Site Visit — Local",
  site_visit_outstation: "Site Visit — Outstation",
  installation_local_two_engineers: "Installation Local · 2 engineers (per engineer)",
  installation_local_solo_full_scope: "Installation Local · Solo, full scope",
  installation_outstation_two_engineers: "Installation Outstation · 2 engineers (per engineer)",
  installation_outstation_solo_with_helper: "Installation Outstation · Solo + local helper",
  installation_outstation_solo_full_scope: "Installation Outstation · Solo, full scope",
};

const SLAB_ORDER = [
  "site_visit_local",
  "site_visit_outstation",
  "installation_local_two_engineers",
  "installation_local_solo_full_scope",
  "installation_outstation_two_engineers",
  "installation_outstation_solo_with_helper",
  "installation_outstation_solo_full_scope",
];

export default function IncentiveSlabs() {
  const [slabs, setSlabs] = useState({});
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/settings/incentive-slabs");
      setSlabs(data.slabs || {});
      setDirty(false);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to load");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const change = (key, val) => {
    setSlabs({ ...slabs, [key]: Number(val) });
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/admin/settings/incentive-slabs", { slabs });
      setSlabs(data.slabs);
      setDirty(false);
      toast.success("Incentive slabs saved");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="incentive-slabs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Settings</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Engineer Incentive Slabs</h1>
          <p className="text-xs text-muted-foreground mt-1">Payable per completed & approved site visit. Travel and stay reimbursements are separate.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={busy}>
            <RefreshCw className={`h-4 w-4 mr-2 ${busy ? "animate-spin" : ""}`} />Reload
          </Button>
          <Button onClick={save} disabled={busy || !dirty} data-testid="save-slabs-btn">
            <HandCoins className="h-4 w-4 mr-2" />Save changes
          </Button>
        </div>
      </div>

      <Card className="p-4 bg-card border-border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SLAB_ORDER.map((key) => (
            <div key={key} className="flex items-center justify-between gap-4 p-3 rounded border border-border bg-secondary/30" data-testid={`slab-${key}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium">{SLAB_LABELS[key]}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{key}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">₹</span>
                <Input type="number" min="0" step="50" value={slabs[key] ?? 0}
                       onChange={(e) => change(key, e.target.value)}
                       className="w-28 text-right font-mono" data-testid={`slab-input-${key}`} />
              </div>
            </div>
          ))}
        </div>
        <div className="text-xs text-muted-foreground mt-4 border-t border-border pt-3">
          Preview total per engineer if they run 1 of each activity in a month:
          <span className="font-mono text-primary ml-2">
            {inr(SLAB_ORDER.reduce((s, k) => s + (slabs[k] || 0), 0))}
          </span>
        </div>
      </Card>

      <Card className="p-4 bg-card border-border">
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Payout rules</div>
        <ul className="text-sm space-y-1 text-muted-foreground list-disc pl-5">
          <li>Payable only after: visit marked completed, photos uploaded, work report submitted, client sign-off captured, manager approval given.</li>
          <li>Travel and stay reimbursements are separate from incentives and handled outside this table.</li>
          <li>Slab changes only affect visits marked as completed <em>after</em> the change.</li>
        </ul>
      </Card>
    </div>
  );
}
